import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RequirePermission } from "@/lib/auth";
import {
  useCategories,
  usePermissions,
  useRoles,
  type RoleRow,
} from "@/lib/rbac";

import { RoleDialog } from "../components/RoleDialog";

export function RolesPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/system/roles" });
  const tab = search.tab ?? "roles";

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RoleRow | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Roles</h1>
          <p className="text-sm text-muted-foreground">
            Roles bundle access for a team. Category grants cover whole sidebar
            sections — including features added later — with per-feature
            overrides for exceptions.
          </p>
        </div>
        <RequirePermission perm="core.roles.manage">
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            New role
          </Button>
        </RequirePermission>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) =>
          navigate({
            to: "/system/roles",
            search: v === "roles" ? {} : { tab: v as "catalog" },
          })
        }
      >
        <TabsList>
          <TabsTrigger value="roles">Roles</TabsTrigger>
          <TabsTrigger value="catalog">Permission catalog</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "roles" ? (
        <RolesTable onEdit={setEditing} />
      ) : (
        <CatalogTable />
      )}

      <RoleDialog
        open={creating || editing !== null}
        role={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
      />
    </div>
  );
}

// ── Roles list ──────────────────────────────────────────────────────────────

function RolesTable({ onEdit }: { onEdit: (r: RoleRow) => void }) {
  const roles = useRoles();
  const [sort, setSort] = useState<{ key: "name" | "members"; dir: "asc" | "desc" } | null>({
    key: "name",
    dir: "asc",
  });

  function toggleSort(key: "name" | "members") {
    setSort((cur) => {
      if (cur?.key !== key) return { key, dir: "asc" };
      if (cur.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  }
  const sortDir = (key: "name" | "members") => (sort?.key === key ? sort.dir : null);

  const rows = useMemo(() => {
    const out = [...(roles.data ?? [])];
    if (!sort) return out;
    out.sort((a, b) => {
      const flip = sort.dir === "asc" ? 1 : -1;
      if (sort.key === "name") return flip * a.name.localeCompare(b.name);
      return flip * (a.member_count - b.member_count);
    });
    return out;
  }, [roles.data, sort]);

  if (roles.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading roles…</p>;
  }
  if (roles.error) {
    return <p className="text-sm text-destructive">{String(roles.error)}</p>;
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table containerClassName="max-h-[calc(100vh-16rem)]">
          <TableHeader>
            <TableRow>
              <TableHead sortable sortDirection={sortDir("name")} onSort={() => toggleSort("name")}>
                Name
              </TableHead>
              <TableHead>Key</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Category grants</TableHead>
              <TableHead align="right">Explicit perms</TableHead>
              <TableHead
                align="right"
                sortable
                sortDirection={sortDir("members")}
                onSort={() => toggleSort("members")}
              >
                Members
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody zebra>
            {rows.length === 0 && <TableEmpty colSpan={6}>No roles yet.</TableEmpty>}
            {rows.map((r) => (
              <TableRow key={r.id} clickable onClick={() => onEdit(r)}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="font-mono text-xs">{r.key}</TableCell>
                <TableCell>
                  <Badge variant={r.is_system ? "neutral" : "info"}>
                    {r.is_system ? "System" : "Custom"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {r.key === "superuser" ? (
                      <span className="text-xs text-muted-foreground">
                        all — handled in code
                      </span>
                    ) : r.category_grants.length === 0 ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      r.category_grants.map((g) => (
                        <Badge
                          key={g.category}
                          variant={g.access === "write" ? "success" : "info"}
                        >
                          {g.category} · {g.access}
                        </Badge>
                      ))
                    )}
                  </div>
                </TableCell>
                <TableCell numeric>
                  {r.key === "superuser" ? "—" : r.permissions.length}
                </TableCell>
                <TableCell numeric>{r.member_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ── Permission catalog (read-only) ──────────────────────────────────────────

function CatalogTable() {
  const perms = usePermissions();
  const categories = useCategories();

  const grouped = useMemo(() => {
    if (!perms.data || !categories.data) return [];
    return categories.data
      .map((cat) => ({
        category: cat.name,
        perms: perms.data
          .filter((p) => cat.features.includes(p.feature))
          .sort((a, b) => a.key.localeCompare(b.key)),
      }))
      .filter((g) => g.perms.length > 0);
  }, [perms.data, categories.data]);

  if (perms.isLoading || categories.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading catalog…</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Every permission the backend declares, grouped by the same categories
        role grants use. Add new ones by declaring them in each feature&apos;s
        permissions.py.
      </p>
      {grouped.map(({ category, perms: rows }) => (
        <Card key={category}>
          <CardContent className="p-0">
            <div className="border-b bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {category}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody zebra>
                {rows.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">{p.key}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          p.action === "read"
                            ? "info"
                            : p.action === "write"
                              ? "success"
                              : "warning"
                        }
                      >
                        {p.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {p.description}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
